import { supabase } from '@/utils/supabase';
import { kpLiveApi } from '@/services/kursi-play-v2/kpLiveApiClient';
import type { KpQualityTier } from '@/types/kursi-play-v2';

export type MistakeReviewItem = {
  attemptId: string;
  scenarioId: string;
  lessonId: string;
  lessonTitle: string;
  scenarioTitle: string;
  qualityTier: KpQualityTier;
  createdAt: string;
  chosenActionLabel: string;
  consequenceNarrative: string | null;
  reinforcements: Array<{ headline: string; body: string; source_anchor: string | null }>;
  groundingAnchors: string[];
};

export type WeakTopicRow = {
  lessonId: string;
  lessonTitle: string;
  avgScore: number;
  weakCount: number;
  riskyCount: number;
  totalAttempts: number;
};

export type MistakeReviewHubData = {
  worldId: string;
  worldName: string;
  hasHistory: boolean;
  recentMistakes: MistakeReviewItem[];
  weakTopics: WeakTopicRow[];
  retryReadyLessonIds: string[];
};

type AttemptRow = {
  id: string;
  scenario_id: string;
  lesson_id: string;
  action_id: string;
  quality_tier: KpQualityTier;
  created_at: string;
};

const QUALITY_SCORE: Record<KpQualityTier, number> = {
  strong: 4,
  acceptable: 3,
  weak: 2,
  risky: 1,
};

export async function loadMistakeReviewHubData(slug: string, userId: string): Promise<MistakeReviewHubData> {
  const home = await kpLiveApi.getWorldHome(slug, userId);
  const lessonTitleById = new Map(home.lessons.map((l) => [l.id, l.title]));

  const base: MistakeReviewHubData = {
    worldId: home.world.id,
    worldName: home.world.world_name,
    hasHistory: false,
    recentMistakes: [],
    weakTopics: [],
    retryReadyLessonIds: [],
  };

  if (!userId || userId.startsWith('guest_')) return base;

  const { data: attemptRows } = await supabase
    .from('kp_user_scenario_attempts')
    .select('id, scenario_id, lesson_id, action_id, quality_tier, created_at')
    .eq('user_id', userId)
    .eq('world_id', home.world.id)
    .order('created_at', { ascending: false })
    .limit(300);

  const attempts = (attemptRows ?? []) as AttemptRow[];
  if (attempts.length === 0) return base;

  const scenarioIds = Array.from(new Set(attempts.map((a) => a.scenario_id)));
  const actionIds = Array.from(new Set(attempts.map((a) => a.action_id)));

  const [{ data: scenarios }, { data: actions }] = await Promise.all([
    supabase.from('kp_scenarios').select('id, title, grounding_map').in('id', scenarioIds),
    supabase.from('kp_scenario_actions').select('id, label').in('id', actionIds),
  ]);

  const scenarioMap = new Map((scenarios ?? []).map((s: any) => [s.id, s]));
  const actionMap = new Map((actions ?? []).map((a: any) => [a.id, a]));

  const weakMistakes = attempts.filter((a) => a.quality_tier === 'weak' || a.quality_tier === 'risky');
  const recentMistakes = weakMistakes.slice(0, 12);

  const mistakeItems: MistakeReviewItem[] = await Promise.all(
    recentMistakes.map(async (attempt) => {
      const [{ data: consequence }, { data: reinforcements }] = await Promise.all([
        supabase.from('kp_scenario_consequences').select('narrative').eq('action_id', attempt.action_id).maybeSingle(),
        supabase
          .from('kp_scenario_reinforcements')
          .select('headline, body, source_anchor')
          .eq('action_id', attempt.action_id)
          .order('order_index', { ascending: true })
          .limit(3),
      ]);

      const scenario = scenarioMap.get(attempt.scenario_id);
      const anchors = Array.isArray(scenario?.grounding_map)
        ? scenario.grounding_map
          .map((g: any) => String(g?.anchor ?? '').trim())
          .filter(Boolean)
          .slice(0, 3)
        : [];

      return {
        attemptId: attempt.id,
        scenarioId: attempt.scenario_id,
        lessonId: attempt.lesson_id,
        lessonTitle: lessonTitleById.get(attempt.lesson_id) ?? 'שיעור',
        scenarioTitle: scenario?.title ?? 'תרחיש',
        qualityTier: attempt.quality_tier,
        createdAt: attempt.created_at,
        chosenActionLabel: actionMap.get(attempt.action_id)?.label ?? 'פעולה שנבחרה',
        consequenceNarrative: consequence?.narrative ?? null,
        reinforcements: (reinforcements ?? []) as any,
        groundingAnchors: anchors,
      };
    }),
  );

  const lessonStats = new Map<string, { total: number; score: number; weak: number; risky: number }>();
  for (const at of attempts) {
    const prev = lessonStats.get(at.lesson_id) ?? { total: 0, score: 0, weak: 0, risky: 0 };
    lessonStats.set(at.lesson_id, {
      total: prev.total + 1,
      score: prev.score + (QUALITY_SCORE[at.quality_tier] ?? 2),
      weak: prev.weak + (at.quality_tier === 'weak' ? 1 : 0),
      risky: prev.risky + (at.quality_tier === 'risky' ? 1 : 0),
    });
  }

  const weakTopics: WeakTopicRow[] = [...lessonStats.entries()]
    .map(([lessonId, st]) => ({
      lessonId,
      lessonTitle: lessonTitleById.get(lessonId) ?? 'שיעור',
      avgScore: Number((st.score / Math.max(st.total, 1)).toFixed(2)),
      weakCount: st.weak,
      riskyCount: st.risky,
      totalAttempts: st.total,
    }))
    .sort((a, b) => {
      if (a.avgScore !== b.avgScore) return a.avgScore - b.avgScore;
      return (b.weakCount + b.riskyCount) - (a.weakCount + a.riskyCount);
    })
    .slice(0, 6);

  return {
    ...base,
    hasHistory: true,
    recentMistakes: mistakeItems,
    weakTopics,
    retryReadyLessonIds: Array.from(new Set(weakMistakes.map((m) => m.lesson_id))).slice(0, 4),
  };
}

export async function getMistakeAttempt(attemptId: string) {
  const { data } = await supabase
    .from('kp_user_scenario_attempts')
    .select('id, scenario_id, lesson_id, action_id, quality_tier, xp_awarded, created_at')
    .eq('id', attemptId)
    .maybeSingle();

  if (!data) throw new Error('Mistake attempt not found');

  const [scenarioRes, actionRes, consequenceRes, reinforcementRes] = await Promise.all([
    supabase.from('kp_scenarios').select('id, title, situation, decision_point, grounding_map').eq('id', data.scenario_id).single(),
    supabase.from('kp_scenario_actions').select('id, label, description').eq('id', data.action_id).single(),
    supabase.from('kp_scenario_consequences').select('narrative, outcome_label, outcome_quality').eq('action_id', data.action_id).maybeSingle(),
    supabase
      .from('kp_scenario_reinforcements')
      .select('reinforcement_type, headline, body, source_anchor')
      .eq('action_id', data.action_id)
      .order('order_index', { ascending: true }),
  ]);

  return {
    attempt: data,
    scenario: scenarioRes.data,
    action: actionRes.data,
    consequence: consequenceRes.data,
    reinforcements: reinforcementRes.data ?? [],
  };
}
